import React from 'react'
import ChartingVoiceStatus, {
  VoicePromptGroup,
} from '@components/pages/Charting/ChartingVoiceStatus.component'
import type { useTxPlanStreamingVoice } from '@hooks/useTxPlanStreamingVoice'
import {
  segmentTxPlanLiveTranscript,
  TxPlanVoiceProcedure,
} from '@utils/tx-plan-voice-apply.utils'

interface TxPlanVoiceStatusProps {
  voice: Pick<
    ReturnType<typeof useTxPlanStreamingVoice>,
    | 'phase'
    | 'keyword'
    | 'backlog'
    | 'message'
    | 'history'
    | 'liveText'
    | 'queued'
  >
  procedures?: TxPlanVoiceProcedure[]
}

const TxPlanTranscriptText = ({
  text,
  procedures,
}: {
  text: string
  procedures: TxPlanVoiceProcedure[]
}): React.ReactElement => {
  const segments = segmentTxPlanLiveTranscript(text, procedures)
  const description = segments.find(
    (segment) => segment.description,
  )?.description
  return (
    <>
      {segments.map((segment, index) => (
        <span
          key={`${segment.text}-${index}`}
          className={segment.description ? 'voice-log-term' : undefined}
        >
          {segment.text}
        </span>
      ))}
      {description && (
        <span className="voice-log-term-description">{description}</span>
      )}
    </>
  )
}

export const TX_PLAN_VOICE_QUICK_PROMPTS: string[] = [
  'Phase one tooth fourteen composite filling mesial occlusal',
  'Code D2392',
  'Tooth fourteen mesial occlusal',
  'Add phase',
]

export const TX_PLAN_VOICE_PROMPT_GROUPS: VoicePromptGroup[] = [
  {
    label: 'Procedures',
    prompts: [
      'Phase one tooth fourteen composite filling mesial occlusal',
      'Crown zirconia tooth three',
      'Code D2392',
      'Tooth fourteen mesial occlusal',
      'Number fourteen mesial occlusal',
      'Fourteen mesial occlusal',
      'Code D2392 tooth five mesial',
      'Search for caries',
      'Remove last',
    ],
  },
  {
    label: 'Phases',
    prompts: ['Add phase', 'Next phase', 'Go to phase two'],
  },
  {
    label: 'Status',
    prompts: ['Accept phase one', 'Reject phase', 'Complete phase two'],
  },
  {
    label: 'Session',
    prompts: ['Undo', 'Pause', 'Resume', 'Stop'],
  },
]

const TxPlanVoiceStatus = ({
  voice,
  procedures = [],
}: TxPlanVoiceStatusProps): React.ReactElement => (
  <ChartingVoiceStatus
    className="tx-plan-voice-panel"
    phase={voice.phase}
    backlog={voice.backlog}
    message={voice.message}
    history={voice.history}
    liveText={voice.liveText}
    queued={voice.queued}
    paused={voice.keyword.paused}
    ariaLabel="Treatment plan voice assistant"
    quickPrompts={TX_PLAN_VOICE_QUICK_PROMPTS}
    promptGroups={TX_PLAN_VOICE_PROMPT_GROUPS}
    renderHistoryText={(text): React.ReactElement => (
      <TxPlanTranscriptText text={text} procedures={procedures} />
    )}
    tip="Pause briefly after each command. The assistant adds procedures to the open plan."
    stateExtra={
      <>
        <span>
          {voice.keyword.phaseIndex >= 0
            ? `Phase ${voice.keyword.phaseIndex + 1}`
            : 'No phase'}
        </span>
        {voice.keyword.pendingAdd && (
          <span>
            Waiting for {voice.keyword.pendingAdd.missingFields.join(' and ')}
          </span>
        )}
      </>
    }
  />
)

export default TxPlanVoiceStatus
